import { push } from "connected-react-router"
import React, { FC, useCallback, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import {
    AIModel,
    AIModelForApp,
    getAIModelID
} from "../../../../../../../Models/API/AIModel"
import { getValue } from "../../../../../../../Models/Client/FetchState"
import { routePaths } from "../../../../../../../Routes"
import { Table } from "resha"
import { AppDetailBase } from "../../AppDetailBase/AppDetailBase"
import { TableHeader } from "../../../../../../Common/Table/TableHeader"
import { Map } from "immutable"
import { hasAppPermission } from "../../../../../../../Models/Client/ProjectRole"
import { useNeqlContextProvider } from "../../../../../../../Hooks/useNeqlContextProvider"
import { MainThunkDispatch } from "../../../../../../../Reducers"
import { EmptyView } from "./Common/EmptyView"
import { getColumnDefinitions } from "./Common/TableColumns"
import { metaAI } from "neql"
import {
    failMessage,
    successMessage
} from "../../../../../../../Actions/Client/MessagesActions"
import { Loader, Button } from "dash-styled"
import { AddAIModelModal } from "./Common/AddAIModelModal"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import { useAssumedRoles } from "../../../../../../../Hooks/useAssumedRoles"
import { useAIModelsForApp } from "../../../../../../../Hooks/useAIModelsForApp"
import { useAIModelDeployment } from "../../../../../../../Hooks/useAIModelDeployment"
import { useAIModels } from "../../../../../../../Hooks/useAIModels"

export interface AIModelListProps {
    orgUsername: string
    appID: string
}

const empty: string[] = []

/**
 * No longer used, kept for future reuse
 */
export const AIModelList: FC<AIModelListProps> = ({ orgUsername, appID }) => {
    const [isModalShown, showModal, hideModal] = useBoolean(false)
    const [selectedModelByName, setSelectedModelByName] =
        useState<AIModel | undefined>()
    const [selectedModelVersionIndex, setSelectedModelVersionIndex] =
        useState<number | "Latest">("Latest")
    const dispatch = useDispatch<MainThunkDispatch>()
    const clientForApp = useNeqlContextProvider("metaAIApp", appID)

    const { data: availableAIModelsByName } = useAIModels(
        { byName: true },
        appID
    )
    const { data: availableAIModelsByVersion } = useAIModels(
        {
            rootId: selectedModelByName?.root_id
        },
        appID
    )

    const { data: modelList, refetch: refetchModels } = useAIModelsForApp(appID)

    const [updateAIModelAllAssignmentsActiveness] =
        metaAI.useUpdateAiModelAllAssignmentsActivenessMutation({
            client: clientForApp
        })
    const [createAIModelForApp, { loading: isCreatingAIModelForApp }] =
        metaAI.useInsertMetaAiAppsMutation({
            client: clientForApp
        })
    const handleModalClose = useCallback(() => {
        setSelectedModelByName(undefined)
        setSelectedModelVersionIndex("Latest")
        hideModal()
    }, [hideModal])

    const handleCreate = useCallback(
        async (
            modelId: string,
            usages: metaAI.Meta_Ai_Assignment_Enum[],
            threshold: number
        ) => {
            createAIModelForApp({
                variables: {
                    objects: [
                        metaAI.Meta_Ai_Assignment_Enum.ActiveLearning,
                        metaAI.Meta_Ai_Assignment_Enum.Prelabel
                    ].map(assigned => ({
                        modelId,
                        active: usages.includes(assigned),
                        assigned,
                        id: appID,
                        threshold,
                        use_latest_ai: selectedModelVersionIndex === "Latest"
                    }))
                }
            })
                .then(() => {
                    dispatch(successMessage("AI model is added"))
                    handleModalClose()
                    refetchModels()
                })
                .catch((e: unknown) =>
                    dispatch(failMessage("Could not add AI model", e))
                )
        },
        [
            appID,
            createAIModelForApp,
            dispatch,
            handleModalClose,
            refetchModels,
            selectedModelVersionIndex
        ]
    )
    const rolesFetch = useAssumedRoles(appID)
    const handleClickRow = useCallback(
        ({ id }: AIModelForApp) =>
            dispatch(push(routePaths.appModelDetail(orgUsername, appID, id))),
        [appID, dispatch, orgUsername]
    )
    const [processingModelIds, setProcessingModelIds] = useState<
        Map<string, true>
    >(Map<string, true>())
    const totalItemCount = modelList?.meta_ai_model.length
    const handleToggleActiveness = useMemo(() => {
        if (!hasAppPermission(getValue(rolesFetch) ?? [], "ai:update")) {
            return undefined
        }

        return (modelId: string, active: boolean) => {
            setProcessingModelIds(ids => ids.set(modelId, true))

            return updateAIModelAllAssignmentsActiveness({
                variables: { appId: appID, modelId, active }
            })
                .then(() => {
                    const message = active
                        ? "Model is activated"
                        : "Model is paused"

                    dispatch(successMessage(message))
                    refetchModels()
                })
                .catch((e: unknown) =>
                    dispatch(failMessage("Could not update model", e))
                )
                .finally(() =>
                    setProcessingModelIds(ids => ids.remove(modelId))
                )
        }
    }, [
        appID,
        dispatch,
        refetchModels,
        rolesFetch,
        updateAIModelAllAssignmentsActiveness
    ])
    const [onRequestPrediction] = useAIModelDeployment(appID)

    const handleRequestPrediction = useMemo(() => {
        if (!hasAppPermission(getValue(rolesFetch) ?? [], "ai:update")) {
            return undefined
        }

        return (modelId: string) => {
            setProcessingModelIds(ids => ids.set(modelId, true))

            return onRequestPrediction([modelId])
                .then(() => {
                    dispatch(successMessage("Model is activated"))
                    refetchModels()
                })
                .catch(e => dispatch(failMessage("Could not update model", e)))
                .finally(() =>
                    setProcessingModelIds(ids => ids.remove(modelId))
                )
        }
    }, [dispatch, onRequestPrediction, refetchModels, rolesFetch])
    const columns = useMemo(
        () =>
            getColumnDefinitions(
                processingModelIds,
                handleToggleActiveness,
                handleRequestPrediction
            ),
        [handleRequestPrediction, handleToggleActiveness, processingModelIds]
    )

    const modelsChoices = useMemo(
        () =>
            (availableAIModelsByName?.meta_ai_model ?? [])
                .filter(model =>
                    modelList?.meta_ai_model.every(
                        myModel => myModel.id !== model.id
                    )
                )
                .filter(
                    (model, index, modelList) =>
                        modelList.findIndex(
                            m => m.root_id === model.root_id
                        ) === index
                ),
        [availableAIModelsByName?.meta_ai_model, modelList?.meta_ai_model]
    )

    const modelsToAdd = useMemo(
        () =>
            (availableAIModelsByVersion?.meta_ai_model ?? []).filter(model =>
                modelList?.meta_ai_model.every(
                    myModel => myModel.id !== model.id
                )
            ),
        [availableAIModelsByVersion?.meta_ai_model, modelList]
    )

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            title="AI Models"
            breadcrumbs={[]}
            fixedContentWidth={false}
        >
            {totalItemCount === undefined ||
            availableAIModelsByName === undefined ? (
                <Loader />
            ) : totalItemCount === 0 ? (
                <EmptyView
                    appID={appID}
                    onAdd={
                        availableAIModelsByName.meta_ai_model.length > 0
                            ? showModal
                            : undefined
                    }
                />
            ) : (
                <>
                    <Button className="w-48" onClick={showModal}>
                        Add new AI
                    </Button>
                    <TableHeader totalCount={totalItemCount} />
                    <Table
                        className="w-full table-fixed"
                        itemCount={totalItemCount}
                        columns={columns}
                        items={modelList?.meta_ai_model}
                        getKey={getAIModelID}
                        checkedItemIds={empty}
                        onRowClick={handleClickRow}
                    />
                </>
            )}
            {isModalShown && (
                <AddAIModelModal
                    selectedModelVersionIndex={selectedModelVersionIndex}
                    selectedModelByName={selectedModelByName}
                    modelChoices={modelsChoices}
                    models={modelsToAdd}
                    onSelectModel={setSelectedModelByName}
                    onSelectModelVersion={setSelectedModelVersionIndex}
                    isProcessing={isCreatingAIModelForApp}
                    onSubmit={handleCreate}
                    onClose={handleModalClose}
                />
            )}
        </AppDetailBase>
    )
}
